import { BookingFormMode } from "PFApp/booking/components/booking_form/booking_form.types";
import { isEditMode } from "PFApp/booking/components/booking_form/booking_form.utils";
import BookingDeleteButton from "PFApp/booking/components/delete_booking_modal/booking_delete_button";
import {
  BookingFormDetails,
  DetailsPanelSource,
  DetailsPanelType,
  OverbookingPotentialBooking,
  useDetailsPanelsActions
} from "PFApp/booking/components/details_panel";
import { useBookingSiblings } from "PFApp/booking/parts/overview/detail_view/booking_detail/booking_detail_content/booking_siblings/use_booking_siblings";
import { BookingDetailSidePanelProfile } from "PFApp/booking/parts/overview/detail_view/booking_detail/booking_detail_side_panel_context/booking_detail_side_panel_context.types";
import { BookingCategoryBadge } from "PFApp/components/booking_category_badge";
import { ActionIcon } from "PFComponents/action_icon";
import { Button } from "PFComponents/button";
import { utc } from "PFCore/helpers/date";
import { getProfileName } from "PFCore/helpers/profile";
import { useBooking } from "PFCore/hooks/queries";
import { useBookingTemplate } from "PFCore/hooks/queries/bookings/booking_templates/use_booking_template";
import { Booking, Profile } from "PFTypes";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { BookingSelectOption } from "../../../../booking_questions/booking_questions.types";
import css from "./booking_item.module.scss";

const checkIsBookingFormAlreadyOpenForThisBookingSiblings = (
  bookingSiblings: Booking[],
  bookingFormInitialBookingId?: number
) => !!bookingFormInitialBookingId && bookingSiblings.some(({ id }) => id === bookingFormInitialBookingId);

type BookingItemProps = {
  bookingId?: number;
  profile: Profile | BookingDetailSidePanelProfile;
  onDeleted?: (entity: Booking) => Promise<void>;
  onEdited?: () => void;
  potentialBooking?: OverbookingPotentialBooking;
  source?: DetailsPanelSource;
  bookingFormDetails?: BookingFormDetails;
};

export const BookingItem = ({
  bookingId,
  onDeleted,
  onEdited,
  profile,
  potentialBooking,
  source,
  bookingFormDetails
}: BookingItemProps) => {
  const { mode: bookingFormMode, initialBookingId: bookingFormInitialBookingId } = bookingFormDetails ?? {};
  const {
    closeTopDetailsPanel,
    closeAllDetailsPanels,
    openDetailsPanel,
    closeDetailsPanel,
    openBookingFormSidePanel,
    closeBookingFormSidePanel
  } = useDetailsPanelsActions();
  const { t } = useTranslation("translation");

  const isBookingFormOpenInEditMode = isEditMode(bookingFormMode);

  const { data: booking } = useBooking(bookingId, { enabled: !!bookingId });

  const { data: bookingSiblings, invalidate: invalidateBookingSiblings } = useBookingSiblings({
    bookingId,
    profileId: booking?.profile_id,
    activityId: booking?.activity?.id,
    enabled: isBookingFormOpenInEditMode
  });

  const {
    booking_category_id: bookingCategoryId,
    title,
    booking_template_id: bookingTemplateId,
    readonly
  } = booking || {};
  const { title: potentialBookingTitle, booking_category_id: potentialBookingCategoryId } =
    potentialBooking || {};

  const { data: bookingTemplate, isLoading: bookingTemplateLoading } = useBookingTemplate(bookingTemplateId, {
    enabled: Number.isInteger(bookingTemplateId)
  });

  const isSingleDayBookingTemplate = useMemo(
    () => bookingTemplate && utc(bookingTemplate.end_date).diff(bookingTemplate.start_date, "days") === 0,
    [bookingTemplate]
  );

  const handleEdit = () => {
    const isBookingFormAlreadyOpenForThisBooking = checkIsBookingFormAlreadyOpenForThisBookingSiblings(
      bookingSiblings,
      bookingFormInitialBookingId
    );
    if (isBookingFormAlreadyOpenForThisBooking) {
      closeTopDetailsPanel();
    } else if (booking) {
      openBookingFormSidePanel(
        {
          bookingId: booking.id,
          ...(profile.suspendedAt
            ? { suspendedProfileDetails: profile as BookingDetailSidePanelProfile }
            : {})
        },
        { onSuccess: onEdited, mode: BookingFormMode.EditSingle, source }
      );
    }
  };

  const handleDelete = (entity: Booking, deleteOptionSelected: BookingSelectOption) => {
    const isBookingFormAlreadyOpenForThisBooking = checkIsBookingFormAlreadyOpenForThisBookingSiblings(
      bookingSiblings,
      bookingFormInitialBookingId
    );
    closeDetailsPanel(DetailsPanelType.Overbookings, profile.id);
    if (isBookingFormAlreadyOpenForThisBooking) {
      const areNoBookingsRemainingInForm =
        bookingSiblings.length === 1 || deleteOptionSelected === BookingSelectOption.All;
      if (areNoBookingsRemainingInForm) {
        closeBookingFormSidePanel();
        closeAllDetailsPanels();
      } else {
        closeTopDetailsPanel();
        invalidateBookingSiblings();
      }
    }

    return onDeleted ? onDeleted(entity) : Promise.resolve();
  };

  return (
    <div className={css.bookingItem}>
      <Button
        small
        kind="blank"
        onClick={() =>
          bookingId &&
          openDetailsPanel(
            {
              id: bookingId,
              type: DetailsPanelType.Booking,
              source
            },
            true
          )
        }
        disabled={!bookingId || !booking}
        text={title || potentialBookingTitle || t("booking")}
      />
      <div className={css.actionsContainer}>
        <BookingCategoryBadge categoryId={bookingCategoryId ?? potentialBookingCategoryId} />
        <ActionIcon
          className={css.action}
          name="edit"
          size="sm"
          onClick={handleEdit}
          disabled={readonly || (Number.isInteger(bookingTemplateId) && bookingTemplateLoading) || !booking}
        />
        <BookingDeleteButton
          kind="icon"
          className={css.action}
          bookingId={booking?.id}
          profileFullName={getProfileName(profile)}
          onDeleted={handleDelete}
          isSingleDayBookingTemplate={isSingleDayBookingTemplate}
          disabled={readonly || !booking}
        />
      </div>
    </div>
  );
};
